import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { formatString } from "../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminBody } from "./bonusAdminBody";
import { toCampaignIn } from "./toWriteBody";
import { LIST_CAMPAIGNS_QUERY_KEY } from "./useListCampaigns";

import type { CampaignOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

async function duplicateCampaign(campaignId: string): Promise<CampaignOut> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  // The list row lacks caps/eligibility/localized strings, so re-fetch the
  // full campaign to copy it faithfully.
  const sourceUrl = API_ROUTES.GET_DYNAMIC_BONUS_CAMPAIGN.replace(
    "{campaign_id}",
    campaignId,
  );
  const sourceResponse = await instance.get(sourceUrl);
  const source = bonusAdminBody<CampaignOut>(sourceResponse);
  if (!source) {
    throw new Error("Campaign not found");
  }

  const payload = toCampaignIn(source, {
    name: formatString(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_COPY_NAME, {
      name: source.name,
    }),
  });

  // Unlike useCreateCampaign, the copy is intentionally NOT auto-activated:
  // duplicates land in `draft` so an admin reviews (and typically reschedules)
  // before the copy can award bonuses.
  const response = await instance.post(
    API_ROUTES.CREATE_DYNAMIC_BONUS_CAMPAIGN,
    payload,
  );
  const created = bonusAdminBody<CampaignOut>(response);
  if (!created) {
    throw new Error("Campaign duplication returned no data");
  }
  return created;
}

export const useDuplicateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation<CampaignOut, Error, string>({
    mutationFn: duplicateCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_CAMPAIGNS_QUERY_KEY });
      message.success(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_DUPLICATED);
    },
    onError: (error) => {
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
    },
  });
};
